import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { wholeContactSchema } from '../../../api/firebase/firestore/contact/validation';
import {
  blackText,
  cardBorder,
  gray,
  primaryButtonBackground,
  primarycolor
} from '../../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../../constants/Location';
import { categories, contact, contactNums } from '../../../constants/enum';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';

interface ContactType {
  companyName: string;
  email: string;
  phone?: string;
  representativeName: string;
  message: string;
  category: number;
}

const styles = {
  cardBorder,
  cardContentPadding,
  cardSize,
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginLeft: '24px'
  },
  label: {
    fontSize: '12px'
  },
  labelFont: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    fontSize: '12px',
    marginLeft: '24px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb
};

const Contact: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('title.common.contact');
  });

  const location = useLocation();
  const [selectMedia] = useState<{ id: string; name: string }>({
    id: (location.state as { id: string; name: string })?.id ?? '',
    name: (location.state as { id: string; name: string })?.name ?? ''
  });
  const [selectMedias] = useState<{
    ids: string;
    names: string;
  }>({
    ids: (location.state as { ids: string; names: string })?.ids ?? '',
    names: (location.state as { ids: string; names: string })?.names ?? ''
  });
  const contactKind =
    (location.state as { contactKind: string })?.contactKind ??
    contact.fromOther;
  const contactNumbers =
    (location.state as { contactNumbers: string })?.contactNumbers ??
    contactNums.zero;
  const type = (location.state as { type: string })?.type;
  const companyName = (location.state as { companyName: string })?.companyName;
  const companyCategory = (location.state as { category: string })?.category;
  const email = (location.state as { email: string })?.email;
  const navigate = useNavigate();

  const localInfo = location.state as {
    companyName: string;
    email: string;
    phone: string;
    category: string;
    message: string;
    contactKind: string;
    contactNumbers: string;
    type: string;
  };

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<ContactType>({
    defaultValues: {
      category: companyCategory !== undefined ? Number(companyCategory) : 0,
      companyName: companyName !== '' ? companyName : localInfo?.companyName,
      email: email !== '' ? email : localInfo?.email,
      message:
        selectMedias.ids !== ''
          ? `${t('contact.mediaName')}：${selectMedias.names}\n${t(
              'contact.mediaId'
            )}：${selectMedias.ids}\n${t('contact.purpose')}`
          : selectMedia.id !== ''
          ? `${t('contact.mediaName')}：${selectMedia.name}\n${t(
              'contact.mediaId'
            )}：${selectMedia.id}\n${t('contact.purpose')}`
          : localInfo?.message ?? '',
      phone: localInfo?.phone !== 'undefined' ? localInfo?.phone : ''
    },
    resolver: yupResolver(wholeContactSchema)
  });

  const onSubmit = (): void => {
    navigate('/common/contact_confirm', {
      state: {
        category: String(watch('category')),
        companyName: watch('companyName'),
        contactKind,
        contactNumbers,
        email: watch('email'),
        message: watch('message'),
        phone: String(watch('phone')),
        type
      }
    });
  };

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === undefined ||
        '' ||
        watch('email') === undefined ||
        '' ||
        Number(watch('category')) === 0 ||
        watch('message') === (undefined ?? '') ||
        Boolean(errors.companyName) ||
        Boolean(errors.email) ||
        Boolean(errors.phone) ||
        Boolean(errors.category) ||
        Boolean(errors.message)
      );
    }
    return false;
  };

  return (
    <>
      <Header />
      <Typography component="div" sx={{ marginTop: '24px' }}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid>
            <Card
              sx={[
                styles.cardSize,
                styles.cardBorder,
                { marginBottom: '40px' }
              ]}
            >
              <CardContent sx={styles.cardContentPadding}>
                <Typography sx={[styles.titleMb, styles.title]} component="div">
                  {t('contact.contact')}
                </Typography>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.companyName)}
                      helperText={errors.companyName?.message}
                      disabled={
                        !(contactKind === contact.fromOther) &&
                        Boolean(companyName && email)
                      }
                      labelText={t('contact.companyName')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder={t('form.xxx')}
                    />
                  )}
                  control={control}
                  name="companyName"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      labelText={t('form.email')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder={t('form.xxx')}
                    />
                  )}
                  control={control}
                  name="email"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.phone)}
                      helperText={errors.phone?.message}
                      labelText={t('contact.phone')}
                      requiredText={t('form.required')}
                      isRequired={false}
                      placeholder={t('form.xxx')}
                    />
                  )}
                  control={control}
                  name="phone"
                />
                <Controller
                  render={({ field }) => (
                    <Typography sx={textMb} align="center" component="div">
                      <Typography align="left" component="div">
                        <Typography
                          color={gray}
                          sx={styles.label}
                          component="span"
                        >
                          {t('contact.category')}
                        </Typography>
                        <Typography component="span" sx={styles.required}>
                          {t('form.required')}
                        </Typography>
                      </Typography>
                      <Typography align="left" component="div">
                        <FormControl>
                          <RadioGroup
                            {...field}
                            aria-labelledby="demo-form-control-label-placement"
                            name="position"
                            sx={{
                              '&.MuiFormGroup-root': {
                                alignContent: 'flex-start'
                              }
                            }}
                          >
                            {(() => {
                              if (contactKind === contact.fromMedia) {
                                if (contactNumbers === contactNums.one) {
                                  return (
                                    <>
                                      <FormControlLabel
                                        value={categories.oneMedia}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label5')}
                                        sx={blackText}
                                        disabled
                                      />
                                    </>
                                  );
                                } else if (
                                  contactNumbers === contactNums.multiple
                                ) {
                                  return (
                                    <>
                                      <FormControlLabel
                                        value={categories.multiMedia}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label6')}
                                        sx={blackText}
                                        disabled
                                      />
                                    </>
                                  );
                                }
                              } else if (contactKind === contact.fromLogin) {
                                if (type.includes('client')) {
                                  return (
                                    <>
                                      <FormControlLabel
                                        value={categories.media}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label7')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.agent}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label8')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.login}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label9')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.feature}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label10')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.opinion}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label11')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.other}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label12')}
                                        sx={blackText}
                                      />
                                    </>
                                  );
                                } else if (type.includes('agent')) {
                                  return (
                                    <>
                                      <FormControlLabel
                                        value={categories.media}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label7')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.client}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label13')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.login}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label9')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.feature}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label10')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.opinion}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label11')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.other}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label12')}
                                        sx={blackText}
                                      />
                                    </>
                                  );
                                } else if (type.includes('publisher')) {
                                  return (
                                    <>
                                      <FormControlLabel
                                        value={categories.client}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label13')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.login}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label9')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.feature}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label10')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.opinion}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label11')}
                                        sx={blackText}
                                      />
                                      <FormControlLabel
                                        value={categories.other}
                                        control={<Radio sx={{ color: gray }} />}
                                        label={t('contact.label12')}
                                        sx={blackText}
                                      />
                                    </>
                                  );
                                }
                              } else if (contactKind === contact.fromOther) {
                                return (
                                  <>
                                    <FormControlLabel
                                      value={categories.service}
                                      control={<Radio sx={{ color: gray }} />}
                                      label={t('contact.label1')}
                                      sx={blackText}
                                    />
                                    <FormControlLabel
                                      value={categories.market}
                                      control={<Radio sx={{ color: gray }} />}
                                      label={t('contact.label2')}
                                      sx={blackText}
                                    />
                                    <FormControlLabel
                                      value={categories.search}
                                      control={<Radio sx={{ color: gray }} />}
                                      label={t('contact.label3')}
                                      sx={blackText}
                                    />
                                    <FormControlLabel
                                      value={categories.other}
                                      control={<Radio sx={{ color: gray }} />}
                                      label={t('contact.label4')}
                                      sx={blackText}
                                    />
                                  </>
                                );
                              }
                            })()}
                          </RadioGroup>
                        </FormControl>
                      </Typography>
                      <FormHelperText
                        sx={styles.helper}
                        error={Boolean(errors.category)}
                      >
                        {errors.category?.message}
                      </FormHelperText>
                    </Typography>
                  )}
                  control={control}
                  name="category"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.message)}
                      helperText={errors.message?.message}
                      isRequired
                      requiredText={t('form.required')}
                      labelText={t('contact.message')}
                      multiline
                      rows={4}
                      fieldType="textarea"
                    />
                  )}
                  control={control}
                  name="message"
                />
                <CardActions
                  sx={{
                    '&.MuiCardActions-root': {
                      marginTop: '30px'
                    },
                    padding: 0
                  }}
                >
                  <Grid container direction="column" alignItems="center">
                    <Button
                      disabled={buttonDisabledFlag()}
                      variant="contained"
                      sx={[primaryButtonBackground, { width: '100%' }]}
                      onClick={() => {
                        void (async () => {
                          setFirstSubmit(true);
                          await handleSubmit(onSubmit)();
                        })();
                      }}
                    >
                      <Typography
                        component="div"
                        color="white"
                        sx={{ fontSize: 15 }}
                      >
                        {t('contact.confirmation')}
                      </Typography>
                    </Button>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Typography>
    </>
  );
};

export default Contact;
